/**
 * Admin Engagement Analytics Console
 * 
 * PURPOSE: Governance-grade, non-financial engagement telemetry console.
 * Pre-MVP observability for discovering how NewAudia is found, explored, and engaged with.
 * 
 * IMPORTANT - THIS IS NOT:
 * - A financial dashboard
 * - A revenue attribution system
 * - A marketing optimization tool
 * 
 * THIS IS:
 * - Behavioral intelligence
 * - Usage signal observation
 * - Early validation radar
 * 
 * Admin Route: /admin/engagement
 * Mode: Read-only
 * Audience: Internal Admin only
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Eye,
  Users,
  Clock,
  MessageSquare,
  Play,
  UserPlus,
  TrendingUp,
  Globe,
  RefreshCw,
  AlertCircle,
  Loader2,
  Calendar as CalendarIcon,
  BarChart3,
  Sparkles,
  Route,
  ExternalLink,
  HelpCircle,
  Headphones,
  ArrowRight,
  Activity,
  Target,
  X,
  Music
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 60) {
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  }
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Observational Disclaimer Banner
const ObservationalDisclaimer = () => (
  <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3 mb-6" data-testid="observational-disclaimer">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-blue-300 font-medium">
          Engagement Analytics (Observational)
        </p>
        <p className="text-xs text-blue-400/80 mt-0.5">
          Metrics shown represent usage and interaction signals. They are non-financial, non-payout, and directional.
        </p>
      </div>
    </div>
  </div>
);

// KPI Card - Now clickable to show chart modal
const KPICard = ({ icon: Icon, label, value, subValue, color = "blue", tooltip, metricName, onClick }) => {
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    amber: "bg-amber-500/20 text-amber-400",
    cyan: "bg-cyan-500/20 text-cyan-400",
    pink: "bg-pink-500/20 text-pink-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    red: "bg-red-500/20 text-red-400",
    indigo: "bg-indigo-500/20 text-indigo-400"
  };

  const isClickable = metricName && onClick;

  return (
    <Card 
      className={`bg-slate-900/60 border-slate-700 ${isClickable ? 'cursor-pointer hover:bg-slate-800/60 hover:border-slate-600 transition-all' : ''}`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={isClickable ? () => onClick(metricName, label) : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-xs text-slate-400" title={tooltip}>{label}</p>
              {isClickable && (
                <BarChart3 className="w-3 h-3 text-slate-500" />
              )}
            </div>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subValue && <p className="text-xs text-slate-500">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Metric Chart Modal
const MetricChartModal = ({ isOpen, onClose, metricName, metricLabel, dateFrom, dateTo, token }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && metricName) {
      fetchChartData();
    }
  }, [isOpen, metricName, dateFrom, dateTo]);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
        exclude_admin: 'true'
      });
      
      const response = await fetch(`${API_URL}/api/admin/engagement/timeseries/${metricName}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChartData(data);
      } else {
        setError('Failed to load chart data');
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  // Color based on metric
  const getChartColor = () => {
    const colors = {
      page_views: '#3b82f6',      // blue
      unique_visitors: '#06b6d4', // cyan
      playback_sessions: '#10b981', // emerald
      ask_audia_sessions: '#a855f7', // purple
      engaged_users: '#3b82f6',   // blue
      accounts_created: '#06b6d4', // cyan
      library_additions: '#10b981' // emerald
    };
    return colors[metricName] || '#6366f1';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            {metricLabel} Over Time
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-400">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          ) : chartData && chartData.data_points.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4 px-2">
                <div>
                  <p className="text-sm text-slate-400">Total</p>
                  <p className="text-2xl font-bold text-white">{formatNumber(chartData.total)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">Period</p>
                  <p className="text-sm text-slate-300">
                    {formatDate(chartData.period_start)} – {formatDate(chartData.period_end)}
                  </p>
                </div>
              </div>
              
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={chartData.data_points}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id={`colorMetric-${metricName}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={getChartColor()} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={getChartColor()} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280"
                      fontSize={11}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getMonth() + 1}/${d.getDate()}`;
                      }}
                    />
                    <YAxis 
                      stroke="#6b7280" 
                      fontSize={11}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: '1px solid #475569',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(val) => formatDate(val)}
                      formatter={(val) => [val, metricLabel]}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke={getChartColor()}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill={`url(#colorMetric-${metricName})`}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <BarChart3 className="w-12 h-12 mb-3 text-slate-600" />
              <p>No data available for this period</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Topic Badge
const TopicBadge = ({ label, count, confidence }) => (
  <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
    <div className="flex items-center gap-2">
      <HelpCircle className="w-4 h-4 text-purple-400" />
      <span className="text-sm text-white">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-400">{count} asks</span>
      {confidence > 0 && (
        <Badge className="bg-purple-500/20 text-purple-300 text-xs">
          {(confidence * 100).toFixed(0)}% conf
        </Badge>
      )}
    </div>
  </div>
);

// Conversion Funnel Step
const FunnelStep = ({ label, count, icon: Icon }) => (
  <div className="flex items-center gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
    <div className="p-2 bg-emerald-500/20 rounded-lg">
      <Icon className="w-5 h-5 text-emerald-400" />
    </div>
    <div className="flex-1">
      <p className="text-sm text-slate-400">{label}</p>
      <p className="text-xl font-bold text-white">{formatNumber(count)}</p>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminEngagementPage = () => {
  const { accessToken: token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [askAudia, setAskAudia] = useState(null);
  const [content, setContent] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [sampleAnalytics, setSampleAnalytics] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const dateRange = searchParams.get('dateRange') || '0'; // Default: Today (0 = today only)
  const [excludeAdmin, setExcludeAdmin] = useState(searchParams.get('excludeAdmin') !== 'false');
  
  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  
  // Chart modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedMetricLabel, setSelectedMetricLabel] = useState('');

  // Update URL params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Calculate date range
  const getDateParams = () => {
    if (isCustomDateRange && customStartDate && customEndDate) {
      return {
        date_from: startOfDay(customStartDate).toISOString(),
        date_to: endOfDay(customEndDate).toISOString()
      };
    }
    
    const endDate = new Date();
    const startDate = new Date();
    
    if (dateRange === '0') {
      // Today only
      return {
        date_from: startOfDay(endDate).toISOString(),
        date_to: endOfDay(endDate).toISOString()
      };
    }
    
    startDate.setDate(startDate.getDate() - parseInt(dateRange));
    return {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString()
    };
  };

  // Handle date range selection
  const handleDateRangeChange = (value) => {
    if (value === 'custom') {
      setIsCustomDateRange(true);
      // Initialize with last 7 days
      setCustomEndDate(new Date());
      setCustomStartDate(subDays(new Date(), 7));
    } else {
      setIsCustomDateRange(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
      updateParams({ dateRange: value });
    }
  };
  
  // Handle metric card click to open chart modal
  const handleMetricClick = (metricName, metricLabel) => {
    setSelectedMetric(metricName);
    setSelectedMetricLabel(metricLabel);
    setChartModalOpen(true);
  };

  // Fetch all engagement data
  const fetchEngagementData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        ...dates,
        exclude_admin: excludeAdmin.toString()
      });
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Determine period for sample analytics (uses 7d/30d/all format)
      const samplePeriod = dateRange === '7' ? '7d' : dateRange === '30' || dateRange === '0' ? '30d' : 'all';
      
      // Fetch all endpoints in parallel (including sample analytics)
      const [overviewRes, trafficRes, askAudiaRes, contentRes, conversionsRes, sampleRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/engagement/overview?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/engagement/traffic?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/engagement/ask-audia?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/engagement/content?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/engagement/conversions?${params}`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/samples?period=${samplePeriod}`, { headers })
      ]);
      
      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (trafficRes.ok) setTraffic(await trafficRes.json());
      if (askAudiaRes.ok) setAskAudia(await askAudiaRes.json());
      if (contentRes.ok) setContent(await contentRes.json());
      if (conversionsRes.ok) setConversions(await conversionsRes.json());
      if (sampleRes.ok) setSampleAnalytics(await sampleRes.json());
      
    } catch (error) {
      console.error('Error fetching engagement data:', error);
      toast.error('Failed to load engagement analytics');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, excludeAdmin, isCustomDateRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchEngagementData();
  }, [fetchEngagementData]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    updateParams({ tab });
  };

  // Refresh handler
  const handleRefresh = () => {
    fetchEngagementData();
    toast.success('Data refreshed');
  };
  
  // Get current date range for chart modal
  const getCurrentDateRange = () => {
    return getDateParams();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6" data-testid="engagement-console-header">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                  <Activity className="w-8 h-8 text-blue-400" />
                  Engagement Analytics
                </h1>
                <p className="text-slate-400">
                  Pre-MVP observability for discovery, exploration, and engagement signals.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Admin Pages Toggle */}
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md">
                  <label className="text-xs text-slate-400 whitespace-nowrap">Include Admin Pages</label>
                  <input 
                    type="checkbox" 
                    checked={!excludeAdmin}
                    onChange={(e) => {
                      setExcludeAdmin(!e.target.checked);
                      updateParams({ excludeAdmin: e.target.checked ? 'false' : 'true' });
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                    data-testid="include-admin-toggle"
                  />
                </div>
                
                {/* Date Range Selector */}
                <Select 
                  value={isCustomDateRange ? 'custom' : dateRange} 
                  onValueChange={handleDateRangeChange}
                >
                  <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700" data-testid="date-range-select">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Today</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Date Range Pickers */}
                {isCustomDateRange && (
                  <div className="flex items-center gap-2">
                    {/* Start Date */}
                    <Popover open={startDatePickerOpen} onOpenChange={setStartDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[120px] bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs px-2"
                          data-testid="start-date-picker"
                        >
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {customStartDate ? format(customStartDate, 'MMM d, yyyy') : 'Start'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
                        <Calendar
                          mode="single"
                          selected={customStartDate}
                          onSelect={(date) => {
                            setCustomStartDate(date);
                            setStartDatePickerOpen(false);
                          }}
                          disabled={(date) => date > new Date() || (customEndDate && date > customEndDate)}
                          initialFocus
                          className="bg-slate-900 text-white"
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <span className="text-slate-500 text-xs">to</span>
                    
                    {/* End Date */}
                    <Popover open={endDatePickerOpen} onOpenChange={setEndDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[120px] bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs px-2"
                          data-testid="end-date-picker"
                        >
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {customEndDate ? format(customEndDate, 'MMM d, yyyy') : 'End'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
                        <Calendar
                          mode="single"
                          selected={customEndDate}
                          onSelect={(date) => {
                            setCustomEndDate(date);
                            setEndDatePickerOpen(false);
                          }}
                          disabled={(date) => date > new Date() || (customStartDate && date < customStartDate)}
                          initialFocus
                          className="bg-slate-900 text-white"
                        />
                      </PopoverContent>
                    </Popover>
                    
                    {/* Apply Custom Range */}
                    <Button
                      size="sm"
                      onClick={fetchEngagementData}
                      className="bg-blue-600 hover:bg-blue-500 text-xs px-3"
                      disabled={!customStartDate || !customEndDate}
                      data-testid="apply-date-range"
                    >
                      Apply
                    </Button>
                  </div>
                )}
                
                <Button onClick={handleRefresh} variant="outline" size="sm" data-testid="refresh-button">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <ObservationalDisclaimer />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="bg-slate-900/60 border border-slate-700 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
                <BarChart3 className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="traffic" className="data-[state=active]:bg-slate-700">
                <Globe className="w-4 h-4 mr-2" />
                Traffic
              </TabsTrigger>
              <TabsTrigger value="ask-audia" className="data-[state=active]:bg-slate-700">
                <MessageSquare className="w-4 h-4 mr-2" />
                Ask Audia
              </TabsTrigger>
              <TabsTrigger value="content" className="data-[state=active]:bg-slate-700">
                <Headphones className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="conversions" className="data-[state=active]:bg-slate-700">
                <Target className="w-4 h-4 mr-2" />
                Conversions
              </TabsTrigger>
              <TabsTrigger value="samples" className="data-[state=active]:bg-slate-700">
                <Music className="w-4 h-4 mr-2" />
                Samples
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {overview && (
                    <>
                      {/* Page View Metrics - FROM INSTRUMENTATION */}
                      {overview.page_view_tracking_enabled ? (
                        <div>
                          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Page View Metrics
                            <Badge className="bg-emerald-500/20 text-emerald-300 text-xs ml-2">Live</Badge>
                            <span className="text-xs text-slate-500 font-normal ml-2">Click cards to view trends</span>
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="page-view-kpis">
                            <KPICard 
                              icon={Eye} 
                              label="Total Page Views" 
                              value={formatNumber(overview.total_page_views)}
                              subValue="All tracked pages"
                              color="blue"
                              metricName="page_views"
                              onClick={handleMetricClick}
                            />
                            <KPICard 
                              icon={Users} 
                              label="Unique Visitors" 
                              value={formatNumber(overview.unique_visitors)}
                              subValue="By anonymous ID"
                              color="cyan"
                              metricName="unique_visitors"
                              onClick={handleMetricClick}
                            />
                            <KPICard 
                              icon={Clock} 
                              label="Avg Visit Duration" 
                              value={formatDuration(overview.avg_visit_duration_seconds)}
                              subValue="Per session"
                              color="amber"
                            />
                            <KPICard 
                              icon={TrendingUp} 
                              label="Pages/Visit" 
                              value={overview.unique_visitors > 0 
                                ? (overview.total_page_views / overview.unique_visitors).toFixed(1)
                                : '—'
                              }
                              subValue="Average depth"
                              color="green"
                            />
                          </div>
                        </div>
                      ) : (
                        <Card className="bg-amber-900/20 border-amber-700/50">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-amber-300">Page View Tracking Not Yet Instrumented</p>
                                <p className="text-xs text-amber-400/80 mt-1">
                                  Total Visits, Unique Visitors, and Visit Duration require frontend page view instrumentation. 
                                  The metrics below show <strong>interaction data</strong> from existing sources.
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Interaction Metrics - FROM EXISTING DATA */}
                      <div>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          Interaction Metrics (From Existing Data)
                          <span className="text-xs text-slate-500 font-normal">Click cards to view trends</span>
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-kpis">
                          <KPICard 
                            icon={Play} 
                            label="Playback Sessions" 
                            value={formatNumber(overview.playback_sessions)}
                            subValue="Content listened"
                            color="emerald"
                            metricName="playback_sessions"
                            onClick={handleMetricClick}
                          />
                          <KPICard 
                            icon={MessageSquare} 
                            label="Ask Audia Sessions" 
                            value={formatNumber(overview.ask_audia_sessions)}
                            subValue="AI conversations"
                            color="purple"
                            metricName="ask_audia_sessions"
                            onClick={handleMetricClick}
                          />
                          <KPICard 
                            icon={Users} 
                            label="Engaged Users" 
                            value={formatNumber(overview.unique_users_engaged)}
                            subValue="Unique authenticated"
                            color="blue"
                            metricName="engaged_users"
                            onClick={handleMetricClick}
                          />
                          <KPICard 
                            icon={UserPlus} 
                            label="Total Accounts" 
                            value={formatNumber(overview.conversion_counts?.total_accounts || 0)}
                            subValue={`${overview.conversion_counts?.accounts_created || 0} in period`}
                            color="cyan"
                            metricName="accounts_created"
                            onClick={handleMetricClick}
                          />
                        </div>
                      </div>

                      {/* Conversion Signals Summary */}
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-emerald-400" />
                            Engagement Breakdown
                          </CardTitle>
                          <CardDescription>
                            User engagement signals from existing data sources
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <FunnelStep 
                              label="Accounts Created" 
                              count={overview.conversion_counts?.accounts_created || 0}
                              icon={UserPlus}
                            />
                            <FunnelStep 
                              label="Users w/ Playback" 
                              count={overview.conversion_counts?.users_with_playback || 0}
                              icon={Play}
                            />
                            <FunnelStep 
                              label="Users w/ Ask Audia" 
                              count={overview.conversion_counts?.users_with_ask_audia || 0}
                              icon={MessageSquare}
                            />
                            <FunnelStep 
                              label="Library Additions" 
                              count={overview.conversion_counts?.library_additions || 0}
                              icon={Target}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Period Info */}
                      <div className="text-xs text-slate-500 text-center">
                        Data period: {formatDate(overview.period_start)} – {formatDate(overview.period_end)}
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Traffic Tab */}
                <TabsContent value="traffic" className="space-y-6">
                  {traffic && (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Routes Table */}
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <Route className="w-5 h-5 text-blue-400" />
                              Top Pages
                            </CardTitle>
                            <CardDescription>Most visited routes</CardDescription>
                          </CardHeader>
                          <CardContent>
                            {traffic.routes?.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-slate-700">
                                    <TableHead className="text-slate-400">Route</TableHead>
                                    <TableHead className="text-slate-400 text-right">Visits</TableHead>
                                    <TableHead className="text-slate-400 text-right">Avg Time</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {traffic.routes.map((route, idx) => (
                                    <TableRow key={idx} className="border-slate-700">
                                      <TableCell className="text-slate-300 font-mono text-sm">
                                        {route.route}
                                      </TableCell>
                                      <TableCell className="text-right text-white font-medium">
                                        {formatNumber(route.visits)}
                                      </TableCell>
                                      <TableCell className="text-right text-slate-400">
                                        {formatDuration(route.avg_duration_seconds)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-slate-500 text-center py-8">No traffic data yet</p>
                            )}
                          </CardContent>
                        </Card>

                        {/* Referrers Table */}
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white flex items-center gap-2">
                              <ExternalLink className="w-5 h-5 text-purple-400" />
                              Top Referrers
                            </CardTitle>
                            <CardDescription>Where visitors come from</CardDescription>
                          </CardHeader>
                          <CardContent>
                            {traffic.referrers?.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-slate-700">
                                    <TableHead className="text-slate-400">Referrer</TableHead>
                                    <TableHead className="text-slate-400 text-right">Visits</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {traffic.referrers.map((ref, idx) => (
                                    <TableRow key={idx} className="border-slate-700">
                                      <TableCell className="text-slate-300 text-sm truncate max-w-[200px]">
                                        {ref.referrer}
                                      </TableCell>
                                      <TableCell className="text-right text-white font-medium">
                                        {formatNumber(ref.visits)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-slate-500 text-center py-8">No referrer data yet</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Device Breakdown */}
                      {traffic.device_breakdown && Object.keys(traffic.device_breakdown).length > 0 && (
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white">Device Breakdown</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(traffic.device_breakdown).map(([device, count]) => (
                                <Badge key={device} className="bg-slate-700 text-white text-sm px-3 py-1">
                                  {device}: {formatNumber(count)}
                                </Badge>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Ask Audia Tab */}
                <TabsContent value="ask-audia" className="space-y-6">
                  {askAudia && (
                    <>
                      {/* KPIs */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <KPICard 
                          icon={MessageSquare} 
                          label="Total Sessions" 
                          value={formatNumber(askAudia.total_sessions)}
                          color="purple"
                        />
                        <KPICard 
                          icon={Sparkles} 
                          label="Avg Questions" 
                          value={askAudia.avg_turn_count?.toFixed(1) || '0'}
                          subValue="per session"
                          color="pink"
                        />
                        <KPICard 
                          icon={Clock} 
                          label="Avg Duration" 
                          value={formatDuration(askAudia.avg_duration_seconds)}
                          color="cyan"
                        />
                        <KPICard 
                          icon={HelpCircle} 
                          label="Topic Categories" 
                          value={formatNumber(askAudia.top_topics?.length || 0)}
                          color="indigo"
                        />
                      </div>

                      {/* Top Question Themes */}
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-purple-400" />
                            Top Question Themes
                          </CardTitle>
                          <CardDescription>
                            What visitors are asking about (topic labels only, raw questions not stored)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {askAudia.top_topics?.length > 0 ? (
                            <div className="space-y-2">
                              {askAudia.top_topics.map((topic, idx) => (
                                <TopicBadge 
                                  key={idx}
                                  label={topic.topic_label}
                                  count={topic.count}
                                  confidence={topic.confidence_avg}
                                />
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-500 text-center py-8">No topic data yet</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Sessions Trend */}
                      {askAudia.sessions_by_day?.length > 0 && (
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white">Sessions Over Time</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-end gap-1 h-32">
                              {askAudia.sessions_by_day.map((day, idx) => {
                                const maxSessions = Math.max(...askAudia.sessions_by_day.map(d => d.sessions));
                                const height = maxSessions > 0 ? (day.sessions / maxSessions * 100) : 0;
                                return (
                                  <div 
                                    key={idx}
                                    className="flex-1 bg-purple-500/60 rounded-t hover:bg-purple-500 transition-colors cursor-pointer"
                                    style={{ height: `${Math.max(height, 4)}%` }}
                                    title={`${day.date}: ${day.sessions} sessions`}
                                  />
                                );
                              })}
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-slate-500">
                              <span>{formatDate(askAudia.sessions_by_day[0]?.date)}</span>
                              <span>{formatDate(askAudia.sessions_by_day[askAudia.sessions_by_day.length - 1]?.date)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Content Tab */}
                <TabsContent value="content" className="space-y-6">
                  {content && (
                    <>
                      {/* Summary KPIs */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <KPICard 
                          icon={Play} 
                          label="Playback Starts" 
                          value={formatNumber(content.total_playback_starts)}
                          color="emerald"
                        />
                        <KPICard 
                          icon={Headphones} 
                          label="Completions" 
                          value={formatNumber(content.total_playback_completions)}
                          color="green"
                        />
                        <KPICard 
                          icon={TrendingUp} 
                          label="Completion Rate" 
                          value={content.total_playback_starts > 0 
                            ? `${((content.total_playback_completions / content.total_playback_starts) * 100).toFixed(1)}%`
                            : '—'
                          }
                          color="cyan"
                        />
                      </div>

                      {/* Content Table */}
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <Headphones className="w-5 h-5 text-emerald-400" />
                            Content Engagement
                          </CardTitle>
                          <CardDescription>
                            Playback metrics for launch content (e.g., "NewAudia – The Next Chapter")
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {content.content_items?.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow className="border-slate-700">
                                  <TableHead className="text-slate-400">Content</TableHead>
                                  <TableHead className="text-slate-400 text-right">Starts</TableHead>
                                  <TableHead className="text-slate-400 text-right">Completions</TableHead>
                                  <TableHead className="text-slate-400 text-right">Completion %</TableHead>
                                  <TableHead className="text-slate-400 text-right">Avg Listen</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {content.content_items.map((item, idx) => (
                                  <TableRow key={idx} className="border-slate-700">
                                    <TableCell className="text-white font-medium">
                                      {item.content_title}
                                    </TableCell>
                                    <TableCell className="text-right text-slate-300">
                                      {formatNumber(item.playback_starts)}
                                    </TableCell>
                                    <TableCell className="text-right text-slate-300">
                                      {formatNumber(item.playback_completions)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <span className={item.completion_rate >= 50 ? 'text-emerald-400' : 'text-slate-400'}>
                                        {item.completion_rate.toFixed(1)}%
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right text-slate-400">
                                      {formatDuration(item.avg_listen_seconds)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-slate-500 text-center py-8">No content playback data yet</p>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </TabsContent>

                {/* Conversions Tab */}
                <TabsContent value="conversions" className="space-y-6">
                  {conversions && (
                    <>
                      {/* Visual Funnel */}
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <Target className="w-5 h-5 text-emerald-400" />
                            Conversion Funnel
                          </CardTitle>
                          <CardDescription>
                            Directional progression signals (counts only, not optimization targets)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            {conversions.signals?.map((signal, idx) => (
                              <React.Fragment key={signal.event_type}>
                                <div className="text-center">
                                  <div className="w-24 h-24 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center mx-auto mb-2">
                                    <span className="text-2xl font-bold text-emerald-400">{formatNumber(signal.count)}</span>
                                  </div>
                                  <p className="text-sm text-slate-300 capitalize">
                                    {signal.event_type.replace(/_/g, ' ')}
                                  </p>
                                </div>
                                {idx < conversions.signals.length - 1 && (
                                  <ArrowRight className="w-6 h-6 text-slate-600 hidden md:block" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Detailed Table */}
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white">Signal Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow className="border-slate-700">
                                <TableHead className="text-slate-400">Event</TableHead>
                                <TableHead className="text-slate-400 text-right">Count</TableHead>
                                <TableHead className="text-slate-400 text-right">First Seen</TableHead>
                                <TableHead className="text-slate-400 text-right">Last Seen</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {conversions.signals?.map((signal, idx) => (
                                <TableRow key={idx} className="border-slate-700">
                                  <TableCell className="text-white capitalize">
                                    {signal.event_type.replace(/_/g, ' ')}
                                  </TableCell>
                                  <TableCell className="text-right text-emerald-400 font-bold">
                                    {formatNumber(signal.count)}
                                  </TableCell>
                                  <TableCell className="text-right text-slate-400">
                                    {formatDateTime(signal.first_seen)}
                                  </TableCell>
                                  <TableCell className="text-right text-slate-400">
                                    {formatDateTime(signal.last_seen)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>

                      {/* Period Info */}
                      <div className="text-xs text-slate-500 text-center">
                        Data period: {formatDate(conversions.period_start)} – {formatDate(conversions.period_end)}
                      </div>
                    </>
                  )}
                </TabsContent>
                
                {/* Samples Tab */}
                <TabsContent value="samples" className="space-y-6">
                  {sampleAnalytics && (
                    <>
                      {/* Platform-wide Sample Metrics */}
                      <div>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Music className="w-4 h-4" />
                          Sample Play Analytics
                          <Badge className="bg-teal-500/20 text-teal-300 text-xs ml-2">Phase 4</Badge>
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sample-kpis">
                          <KPICard 
                            icon={Play} 
                            label="Total Plays (All Time)" 
                            value={formatNumber(sampleAnalytics.total_plays_all_time)}
                            subValue="Guest sample plays"
                            color="emerald"
                          />
                          <KPICard 
                            icon={Play} 
                            label="Plays Last 30 Days" 
                            value={formatNumber(sampleAnalytics.total_plays_last_30_days)}
                            subValue="Recent activity"
                            color="cyan"
                          />
                          <KPICard 
                            icon={Play} 
                            label="Plays Last 7 Days" 
                            value={formatNumber(sampleAnalytics.total_plays_last_7_days)}
                            subValue="This week"
                            color="blue"
                          />
                          <KPICard 
                            icon={Users} 
                            label="Unique Sessions" 
                            value={formatNumber(sampleAnalytics.unique_sessions_all_time)}
                            subValue="Distinct listeners"
                            color="purple"
                          />
                        </div>
                      </div>
                      
                      {/* Sample Coverage Stats */}
                      <div>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Sample Coverage
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <KPICard 
                            icon={Headphones} 
                            label="Books with Samples" 
                            value={formatNumber(sampleAnalytics.manuscripts_with_samples)}
                            subValue="Active samples"
                            color="emerald"
                          />
                          <KPICard 
                            icon={Headphones} 
                            label="Books without Samples" 
                            value={formatNumber(sampleAnalytics.manuscripts_without_samples)}
                            subValue="No sample configured"
                            color="amber"
                          />
                          <KPICard 
                            icon={Clock} 
                            label="Avg Listen Duration" 
                            value={formatDuration(sampleAnalytics.avg_listen_duration_seconds)}
                            subValue="Per play session"
                            color="blue"
                          />
                        </div>
                      </div>
                      
                      {/* Top 10 Most-Sampled Titles */}
                      {sampleAnalytics.top_sampled_titles && sampleAnalytics.top_sampled_titles.length > 0 && (
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                              <TrendingUp className="w-4 h-4 text-emerald-400" />
                              Top 10 Most-Sampled Titles
                            </CardTitle>
                            <CardDescription>Ranked by total sample plays</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {sampleAnalytics.top_sampled_titles.map((title, index) => (
                                <div 
                                  key={title.manuscript_id} 
                                  className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg font-bold text-slate-500 w-6">#{index + 1}</span>
                                    <span className="text-sm text-white truncate max-w-[300px]">{title.title}</span>
                                  </div>
                                  <Badge className="bg-emerald-500/20 text-emerald-300">
                                    {formatNumber(title.total_plays)} plays
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Per-Manuscript Breakdown Table */}
                      {sampleAnalytics.per_manuscript_breakdown && sampleAnalytics.per_manuscript_breakdown.length > 0 && (
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                              <BarChart3 className="w-4 h-4 text-blue-400" />
                              Per-Manuscript Breakdown
                            </CardTitle>
                            <CardDescription>Detailed stats for each book with sample plays</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <Table>
                              <TableHeader>
                                <TableRow className="border-slate-700">
                                  <TableHead className="text-slate-400">Book Title</TableHead>
                                  <TableHead className="text-right text-slate-400">Total Plays</TableHead>
                                  <TableHead className="text-right text-slate-400">Last 7 Days</TableHead>
                                  <TableHead className="text-right text-slate-400">Unique Sessions</TableHead>
                                  <TableHead className="text-right text-slate-400">Avg Duration</TableHead>
                                  <TableHead className="text-right text-slate-400">Sample Segments</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sampleAnalytics.per_manuscript_breakdown.map((ms) => (
                                  <TableRow key={ms.manuscript_id} className="border-slate-700">
                                    <TableCell className="text-white font-medium max-w-[200px] truncate" title={ms.title}>
                                      {ms.title}
                                    </TableCell>
                                    <TableCell className="text-right text-emerald-400 font-semibold">
                                      {formatNumber(ms.total_plays)}
                                    </TableCell>
                                    <TableCell className="text-right text-cyan-400">
                                      {formatNumber(ms.plays_last_7_days)}
                                    </TableCell>
                                    <TableCell className="text-right text-slate-400">
                                      {formatNumber(ms.unique_sessions)}
                                    </TableCell>
                                    <TableCell className="text-right text-slate-400">
                                      {formatDuration(ms.avg_listen_duration_seconds)}
                                    </TableCell>
                                    <TableCell className="text-right text-slate-400">
                                      {ms.sample_segment_count}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Empty State */}
                      {(!sampleAnalytics.per_manuscript_breakdown || sampleAnalytics.per_manuscript_breakdown.length === 0) && (
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardContent className="p-8 text-center">
                            <Music className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-300 mb-2">No Sample Play Data Yet</h3>
                            <p className="text-sm text-slate-500">
                              Sample play events will appear here once guests start listening to book samples.
                            </p>
                          </CardContent>
                        </Card>
                      )}
                      
                      {/* Period Info */}
                      <div className="text-xs text-slate-500 text-center">
                        Data period: {formatDate(sampleAnalytics.period_start)} – {formatDate(sampleAnalytics.period_end)}
                      </div>
                    </>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </main>
      </div>

      <AdminMobileBottomNav />
      <UniversalFooter />
      
      {/* Metric Chart Modal */}
      <MetricChartModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        metricName={selectedMetric}
        metricLabel={selectedMetricLabel}
        dateFrom={getCurrentDateRange().date_from}
        dateTo={getCurrentDateRange().date_to}
        token={token}
      />
    </div>
  );
};

export default AdminEngagementPage;
